import {
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  Header,
  SpaceBetween,
  Modal,
  Box,
} from "@amzn/awsui-components-react";
import React from "react";
import { SummaryItem } from "./SummaryItem";
import { useSummaryData } from "../../hooks/useSummaryData";
import ExternalLinks from "../ExternalLinks";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../types/documents";
import { AccountingEventsGroup } from "../../types/events";
import HealthStatusIndicator from "../common/HealthStatusIndicator";
import { DlqInfo } from "../../hooks/useFetchDlqInfo";
import DlqStatusIndicator from "../dlq/DlqStatusIndicator";
import { screamingSnakeToTitleCase } from "../../helpers/dbdUtils";
import useFolders from "../../hooks/useFolders";
import { useFolderUI } from "../../hooks/useFolderUI";
import { FolderModal } from "../folders/FolderModal";
import { SavedSearchItem } from "../../types/common";
import { CanonicalBusinessEvent } from "../../types/cbes";
import {
  hasBundleOrder,
  hasMultipleProducts,
} from "../../helpers/bundleRevenueUtils";
import { BundleRevenueTimeline } from "../cbe/BundleRevenueTimeline";
import useShadowTest from "../../hooks/useShadowTest";
import { Stage } from "../../lusca-search-api/generated-src";
import useShadowTestResult from "../../hooks/useShadowTestResult";
import useClassifications from "../../hooks/useClassifications";
import ShadowTestResultModal from "./ShadowTestResultModal";
import { MatchSetData } from "../../hooks/useFetchMatchSets";
import MatchSetStatusIndicator from "../common/MatchSetStatusIndicator";
import { isRevenueBookedByBothFlows } from "../../helpers/flowTypeUtils";
import { setModalParam, clearModalParam, readInitialModal } from "../../helpers/modalUrlUtils";

interface SummaryProps {
  eventData: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[];
  dlqInfo: DlqInfo;
  cbeData?: CanonicalBusinessEvent[];
  matchSetData?: MatchSetData;
}

export default function Summary({
  eventData,
  dlqInfo,
  cbeData = [],
  matchSetData,
}: SummaryProps) {
  const { folders, addTransactions, saveFolder } = useFolders();
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI(true);

  const [isBundleTimelineModalVisible, setIsBundleTimelineModalVisible] =
    React.useState(false);

  const {
    isLoading: isShadowTestLoading,
    error: shadowTestTriggerError,
    trigger: triggerShadowTest,
  } = useShadowTest();

  const [isShadowTestResultModalVisible, setIsShadowTestResultModalVisible] =
    React.useState(() => readInitialModal().modal === "shadow-test");

  const {
    stage,
    orderId,
    countryCode,
    healthStatus,
    flowSteps,
    orderDate,
    glProductGroup,
    consumptionType,
    fulfillmentType,
    paymentMethod,
    isFreeTrial,
    isBundleImmediate,
    isSeasonPassBundle,
    isPreorderBundle,
    getGlMapping,
    lastItem,
    isHealthLoading,
    declaredCountryCode,
  } = useSummaryData(eventData, dlqInfo, cbeData, matchSetData);

  const {
    result: shadowTestResult,
    isLoading: isShadowTestResultLoading,
    isFetching: isShadowTestResultFetching,
    error: shadowTestResultError,
    refetch: refetchShadowTestResult,
  } = useShadowTestResult({
    stage,
    transactionId: orderId,
  });

  const classifications = useClassifications(stage);

  const isPaymentDocument = !!lastItem?.digitalPaymentDocument;

  const formattedConsumptionType = consumptionType
    ? screamingSnakeToTitleCase(consumptionType.toUpperCase())
    : null;

  const handleFolderSelect = () => {
    if (selectedFolderId && orderId) {
      const newTransaction: SavedSearchItem = {
        date: new Date().toISOString(),
        id: orderId,
        stage,
        status: healthStatus,
        country: countryCode || "",
        gl: String(glProductGroup || ""),
        usecase: "",
        paymentMethod: paymentMethod || "",
        labels: [],
        consumptionTypes: [],
      };

      const result = addTransactions(selectedFolderId, [newTransaction]);

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });
      handleModalDismiss();
    }
  };

  // Check if bundle revenue timeline should be shown
  // Support both immediate bundles and season pass bundles
  const shouldShowBundleTimeline =
    cbeData &&
    cbeData.length > 0 &&
    cbeData.some(
      (cbe) =>
        cbe.internalAttributes?.IS_BUNDLE_IMMEDIATE === "true" ||
        cbe.internalAttributes?.IS_SEASON_PASS_BUNDLE === "true"
    ) &&
    hasBundleOrder(cbeData) &&
    hasMultipleProducts(cbeData);

  const dbdData = React.useMemo(() => {
    const dbd = eventData.find(
      (item): item is DigitalBusinessDocument =>
        "body" in item && item.documentType === "DigitalBusinessDocument"
    );

    if (!dbd)
      return {
        products: undefined,
        totalOrderValue: undefined,
        productCount: undefined,
      };

    const products = dbd.body?.body?.products;
    const payments = dbd.body?.body?.payments;

    let totalOrderValue: number | undefined;
    if (payments) {
      const firstPayment = Object.values(payments)[0];
      const amountStr = firstPayment?.paymentMethods
        ? Object.values(firstPayment.paymentMethods)[0]?.amount
            ?.customerTotalAmountWithoutTax?.value
        : undefined;
      const parsed = amountStr ? parseFloat(amountStr) : undefined;
      totalOrderValue = parsed && !Number.isNaN(parsed) ? parsed : undefined;
    }

    const productCount = products ? Object.keys(products).length : undefined;

    return { products, totalOrderValue, productCount };
  }, [eventData]);

  const getShadowTestEventData = () => {
    const accountingEventsGroup = eventData.find(
      (item): item is AccountingEventsGroup => "accountingEvents" in item
    );
    const firstEvent = accountingEventsGroup
      ? Object.values(accountingEventsGroup.accountingEvents)[0]
      : null;
    return {
      auid: firstEvent?.accountingUniqueId || "",
      subscriptionId: firstEvent?.subscriptionId || "",
      subscriptionPeriod: firstEvent?.subscriptionPeriod || "",
      digitalOrderID: firstEvent?.orderId || "",
      deliveryPacketItemId: firstEvent?.deliveryPacketItemId || "",
      financeTagAuthReferenceId: firstEvent?.financeTagAuthReferenceId || "",
      subCountryCode: firstEvent?.declaredCountryCode || "",
    };
  };

  const generateShadowTestCommand = () => {
    try {
      const { auid, subscriptionId, subscriptionPeriod, digitalOrderID,
              deliveryPacketItemId, financeTagAuthReferenceId, subCountryCode } = getShadowTestEventData();
      const marketplace = countryCode.replace("_PROD", "");

      return `brazil-build shadow-test --args="-m ${marketplace} -cc ${subCountryCode} -gl ${
        glProductGroup ?? ""
      } -o ${orderId}|${auid}|${subscriptionId}|${subscriptionPeriod}|${deliveryPacketItemId}|${digitalOrderID}|${financeTagAuthReferenceId}|"`;
    } catch (cmdError) {
      return `brazil-build shadow-test --args="-m ${countryCode} -gl ${
        glProductGroup ?? ""
      } -o ${orderId}"`;
    }
  };

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Container
        header={
          <Header
            variant="h1"
            description="Summary"
            actions={
              <SpaceBetween direction="horizontal" size="xxs">
                <Button
                  disabled={!orderId}
                  onClick={() => { setIsShadowTestResultModalVisible(true); setModalParam("shadow-test"); }}
                  iconName="status-info"
                >
                  {shadowTestResult
                    ? `Shadow Test: ${
                        shadowTestResult.passed ? "PASS" : "FAIL"
                      }`
                    : "Shadow Test Results"}
                </Button>
                {shouldShowBundleTimeline && (
                  <Button
                    onClick={() => setIsBundleTimelineModalVisible(true)}
                    iconName="status-in-progress"
                  >
                    Bundle Revenue Timeline
                  </Button>
                )}
                <Button
                  onClick={() => setIsModalVisible(true)}
                  disabled={isPaymentDocument}
                >
                  Add to Folder
                </Button>
              </SpaceBetween>
            }
          >
            {orderId}
          </Header>
        }
      >
        <ColumnLayout columns={5} borders="vertical">
          <SummaryItem label="Stage" value={stage} />
          <SummaryItem
            label="GL"
            value={glProductGroup ? getGlMapping(glProductGroup) : "-"}
          />
          <SummaryItem label="Country" value={countryCode} />
          {paymentMethod && (
            <SummaryItem
              label="Payment"
              value={
                isFreeTrial ? `Free Trial (${paymentMethod})` : paymentMethod
              }
            />
          )}
          <SummaryItem
            label="Order Date (UTC)"
            value={
              orderDate
                ? new Date(orderDate).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "UTC",
                  })
                : "-"
            }
          />
          {formattedConsumptionType && (
            <SummaryItem label="Consumption" value={formattedConsumptionType} />
          )}
          {fulfillmentType && (
            <SummaryItem
              label="Fulfillment"
              value={screamingSnakeToTitleCase(fulfillmentType)}
            />
          )}
          {/* TEMP FIX: Hide bundle flags when all are "No" to reduce clutter.
             Only show the ones that are "Yes". Remove this when bundle display is redesigned. */}
          {isBundleImmediate && (
            <SummaryItem label="Immediate Bundle" value="Yes" />
          )}
          {isSeasonPassBundle && (
            <SummaryItem label="Season Pass Bundle" value="Yes" />
          )}
          {isPreorderBundle && (
            <SummaryItem label="Preorder Bundle" value="Yes" />
          )}
          <SummaryItem
            label="Status"
            value={
              <HealthStatusIndicator
                healthStatus={healthStatus}
                flowSteps={flowSteps}
                isLoading={isHealthLoading}
                eventData={eventData}
                cbeData={cbeData}
              />
            }
          />
          {matchSetData && (
            <SummaryItem
              label="Match Sets"
              value={<MatchSetStatusIndicator matchSetData={matchSetData} isDualFlow={isRevenueBookedByBothFlows(eventData, cbeData)} />}
            />
          )}
          <SummaryItem
            label="DLQ Status"
            value={
              <DlqStatusIndicator
                status={dlqInfo.status}
                dlqItems={dlqInfo.dlqItems}
                stage={stage}
              />
            }
          />
          <SummaryItem
            label="External Links"
            value={
              <ExternalLinks
                {...lastItem}
                cbeIdempotenceId={
                  isPaymentDocument
                    ? cbeData[0]?.idempotenceId
                    : undefined
                }
              />
            }
          />
        </ColumnLayout>
      </Container>

      <FolderModal
        isModalVisible={isModalVisible}
        isFolderModalVisible={isFolderModalVisible}
        isShareModalVisible={false}
        selectedFolderId={selectedFolderId}
        folderName={folderName}
        error={error}
        folders={folders}
        sharingFolder={null}
        onModalDismiss={handleModalDismiss}
        onCreateFolder={handleCreateFolder}
        onFolderSelect={handleFolderSelect}
        onFolderModalDismiss={handleFolderModalDismiss}
        onShareModalDismiss={() => {}}
        onSaveFolder={() => handleSaveFolder(saveFolder)}
        setSelectedFolderId={setSelectedFolderId}
        setFolderName={setFolderName}
      />

      {shouldShowBundleTimeline && (
        <Modal
          onDismiss={() => setIsBundleTimelineModalVisible(false)}
          visible={isBundleTimelineModalVisible}
          size="max"
          footer={
            <Box float="right">
              <Button
                variant="primary"
                onClick={() => setIsBundleTimelineModalVisible(false)}
              >
                Close
              </Button>
            </Box>
          }
          header="Bundle Revenue Timeline"
        >
          <BundleRevenueTimeline
            cbes={cbeData}
            totalOrderValue={dbdData.totalOrderValue}
            productCount={dbdData.productCount}
            dbdProducts={dbdData.products}
          />
        </Modal>
      )}

      <ShadowTestResultModal
        visible={isShadowTestResultModalVisible}
        onDismiss={() => { setIsShadowTestResultModalVisible(false); clearModalParam(); }}
        result={shadowTestResult}
        isLoading={isShadowTestResultLoading}
        isRefreshing={isShadowTestResultFetching}
        error={shadowTestResultError}
        onRefresh={refetchShadowTestResult}
        cliCommand={generateShadowTestCommand()}
        classifications={classifications}
        onTriggerTest={() => {
          const { auid, subscriptionId, subscriptionPeriod, digitalOrderID,
                  deliveryPacketItemId, financeTagAuthReferenceId, subCountryCode } = getShadowTestEventData();
          triggerShadowTest({
            stage: stage as Stage,
            marketplace: countryCode?.replace("_PROD", "") || "",
            glProductGroupId: String(glProductGroup || ""),
            transactionIds: [orderId!],
            countryCode: declaredCountryCode || subCountryCode || countryCode?.replace("_PROD", "") || "",
            auid: auid || undefined,
            subscriptionId: subscriptionId || undefined,
            periodNumber: subscriptionPeriod ? String(subscriptionPeriod) : undefined,
            deliveryPacketItemId: deliveryPacketItemId ? String(deliveryPacketItemId) : undefined,
            digitalOrderID: digitalOrderID || undefined,
            financeTagAuthReferenceId: financeTagAuthReferenceId || undefined,
          });
        }}
        isTriggerLoading={isShadowTestLoading}
        triggerError={shadowTestTriggerError}
        canTrigger={!isPaymentDocument && !!orderId}
      />
    </>
  );
}
